import type { NavigationContext } from './types';
import { EligibleResourceType } from '@/data/subsidy/policies/interfaces';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';

type StepId = RefundRequestWizardStepIds;
type StepResolver = (ctx: NavigationContext) => StepId | null;

export interface StepNode {
  shouldInclude?: (ctx: NavigationContext) => boolean;
  next: StepId | StepResolver;
  previous: StepId | StepResolver;
}

export const navigationGraph: Record<StepId, StepNode> = {
  [RefundRequestWizardStepIds.ResourceType]: {
    shouldInclude: (ctx) => ctx.hasAvailableResourceApprovals,
    next: (ctx) => {
      if (!ctx.hasOnePolicy) return RefundRequestWizardStepIds.SelectPolicy;

      return ctx.eligibleResourceType === EligibleResourceType.Catalog
        ? RefundRequestWizardStepIds.CatalogForm
        : RefundRequestWizardStepIds.ItemSource;
    },
    previous: () => null,
  },

  [RefundRequestWizardStepIds.SelectPolicy]: {
    shouldInclude: (ctx) => !ctx.hasOnePolicy,
    next: (ctx) => {
      const selectedPolicy =
        ctx.values?.[RefundRequestWizardStepIds.SelectPolicy] ??
        ctx.values?.selectedPolicy;
      const resourceType =
        selectedPolicy?.configuration?.eligible_resource_type;

      return resourceType === EligibleResourceType.Catalog
        ? RefundRequestWizardStepIds.CatalogForm
        : RefundRequestWizardStepIds.ItemSource;
    },
    previous: (ctx) =>
      ctx.hasAvailableResourceApprovals
        ? RefundRequestWizardStepIds.ResourceType
        : null,
  },

  [RefundRequestWizardStepIds.ItemSource]: {
    shouldInclude: (ctx) =>
      ctx.eligibleResourceType === EligibleResourceType.ThreeSixty,
    next: (ctx) => {
      const itemSource = ctx?.values?.[RefundRequestWizardStepIds.ItemSource];

      return itemSource === EligibleResourceType.Catalog
        ? RefundRequestWizardStepIds.CatalogForm
        : RefundRequestWizardStepIds.ExternalResourceType;
    },
    previous: (ctx) =>
      ctx?.hasOnePolicy
        ? ctx?.hasAvailableResourceApprovals
          ? RefundRequestWizardStepIds.ResourceType
          : null
        : RefundRequestWizardStepIds.SelectPolicy,
  },

  [RefundRequestWizardStepIds.CatalogForm]: {
    next: () => RefundRequestWizardStepIds.Summary,
    previous: (ctx) => {
      if (ctx.eligibleResourceType === EligibleResourceType.ThreeSixty) {
        return RefundRequestWizardStepIds.ItemSource;
      }

      if (!ctx.hasOnePolicy) {
        return RefundRequestWizardStepIds.SelectPolicy;
      }

      return ctx.hasAvailableResourceApprovals
        ? RefundRequestWizardStepIds.ResourceType
        : null;
    },
  },

  [RefundRequestWizardStepIds.ExternalResourceType]: {
    next: () => RefundRequestWizardStepIds.ExternalItemForm,
    previous: () => RefundRequestWizardStepIds.ItemSource,
  },

  [RefundRequestWizardStepIds.ExternalItemForm]: {
    next: () => RefundRequestWizardStepIds.Summary,
    previous: () => RefundRequestWizardStepIds.ExternalResourceType,
  },

  [RefundRequestWizardStepIds.Summary]: {
    next: () => null,
    previous: (ctx) => {
      const itemSource = ctx.values?.[RefundRequestWizardStepIds.ItemSource];

      return itemSource === EligibleResourceType.ThreeSixty
        ? RefundRequestWizardStepIds.ExternalItemForm
        : RefundRequestWizardStepIds.CatalogForm;
    },
  },
};

/**
 * Define a ordem canônica dos steps no fluxo.
 * Usado para calcular o step inicial.
 */
export const stepOrder: StepId[] = [
  RefundRequestWizardStepIds.ResourceType,
  RefundRequestWizardStepIds.SelectPolicy,
  RefundRequestWizardStepIds.ItemSource,
  RefundRequestWizardStepIds.CatalogForm,
  RefundRequestWizardStepIds.ExternalResourceType,
  RefundRequestWizardStepIds.ExternalItemForm,
  RefundRequestWizardStepIds.Summary,
];
